import { zodResolver } from "@hookform/resolvers/zod";
import { PracticeManagement } from "@prisma/client";
import * as RadioGroup from "@radix-ui/react-radio-group";
import type { GetServerSidePropsContext } from "next";
import type { Metadata } from "next";
import { useRouter } from "next/router";
import React, { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

import prisma from "@calcom/prisma";
import { trpc } from "@calcom/trpc/react";
import type { inferSSRProps } from "@calcom/types/inferSSRProps";
import { TextField, Form, Select, Button, Switch } from "@calcom/ui";

import { practiceList } from "@lib/constant";
import { TERMINAL_DEVICE_PRICE } from "@lib/constant";
import {
  formatFreeTerminalSummary,
  formatSaasDiscountValue,
  freeTerminalMessage,
  freeTerminalUnits,
  saasDiscountAmount,
  terminalCharge,
} from "@lib/onboarding/promoPricing";
import type { FreeTerminalMode, SaasDiscountType } from "@lib/onboarding/promoPricing";
import { formatPhoneNumber } from "@lib/practice/helper";

import CustomHead from "@components/CustomHead";
import { GreenShieldCheckIcon } from "@components/Icons";
import PayrixForm from "@components/payment/PayrixForm";
import QuantitySelector from "@components/ui/QuantitySelector";

// Terminal device pricing

export const metadata: Metadata = {
  title: "Onboarding | QuantaPay",
};

export type PaymentPageProps = inferSSRProps<typeof getServerSideProps>;

type AppliedPromoCodeData = {
  code: string;
  description: string | null;
  discount: number;
  saasDiscountType: SaasDiscountType;
  freeTerminalMode: FreeTerminalMode;
  freeTerminalCount: number;
  feeType: "ONLINE_TERMINAL" | "CREDIT_DEBIT" | null;
  onlineFee: number | null;
  terminalFee: number | null;
  creditFee: number | null;
  debitFee: number | null;
  useDiscountPlan: boolean;
};

function formatFeePct(n: number | null) {
  return n != null && Number.isFinite(n) ? `${n}%` : "—";
}

function hasPromoFeePair(a: number | null, b: number | null) {
  return a != null || b != null;
}

type FormValues = {
  firstName: string;
  lastName: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  email: string;
  phone: string;
  contactEmail: string;
  contactPhone: string;
  locationName: string;
  practiceManagementSoftware: PracticeManagement;
  timeZone: string;
  selectedPlan: number;
  terminalQuantity: number;
  agreeToTerms: boolean;
};

const formValueSchema = z.object({
  firstName: z.string().min(3, "Required"),
  lastName: z.string().min(3, "Required"),
  address: z.string().min(3, "Required"),
  city: z.string().min(2, "Required"),
  state: z.string().min(2, "Required"),
  zip: z.string().min(2, "Required"),
  email: z.string().email(),
  phone: z.string().min(3, "Required"),
  contactEmail: z.string().email("Invalid contact email"),
  contactPhone: z.string().min(3, "Required"),
  locationName: z.string().min(3, "Required"),
  practiceManagementSoftware: z.nativeEnum(PracticeManagement),
  timeZone: z.string().min(3, "Required"),
  selectedPlan: z.number().min(0, "Required"),
  terminalQuantity: z.number().min(0, "Required"),
  agreeToTerms: z.boolean().refine((val) => val === true, {
    message: "You must agree to the Terms and Conditions, Privacy Policy, and Business Associate Agreement",
  }),
});

export default function Onboarding({ apiKey, paymentEnv, merchant, onboardingSettings }: PaymentPageProps) {
  const router = useRouter();

  const {
    onboardingPlans,
    logo,
    brandColor,
    isForDentive,
    isFirstTerminalFree: FIRST_TERMINAL_FREE,
    activePaymentForm,
  } = onboardingSettings;

  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState("");
  const [promoCodeData, setPromoCodeData] = useState<AppliedPromoCodeData | null>(null);
  const [promoCodeError, setPromoCodeError] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [showQsavePlans, setShowQsavePlans] = useState(false);
  const [showDiscountPlans, setShowDiscountPlans] = useState(false);

  const filteredPlans = useMemo(() => {
    return onboardingPlans.filter(
      (plan) => plan.isDiscount === showDiscountPlans && plan.type === (showQsavePlans ? "QSAVE" : "STANDARD")
    );
  }, [onboardingPlans, showQsavePlans, showDiscountPlans]);

  useEffect(() => {
    if (filteredPlans.length === 0) {
      setShowQsavePlans(!showQsavePlans);
    }
  }, [filteredPlans]);

  const defaultStandardPlan = onboardingPlans.find((plan) => plan.type === "STANDARD") ?? onboardingPlans[0];

  const form = useForm<FormValues>({
    defaultValues: {
      firstName: "",
      lastName: "",
      address: "",
      city: "",
      state: "",
      zip: "",
      email: "",
      phone: "",
      contactEmail: "",
      contactPhone: "",
      locationName: "",
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      selectedPlan: 0,
      terminalQuantity: defaultStandardPlan.minTerminalQuantity,
      agreeToTerms: false,
    },
    mode: "onChange",
    resolver: zodResolver(formValueSchema),
  });

  const { register, setValue, getValues, trigger, watch } = form;

  // Watch for changes in selected plan and terminal quantity
  const selectedPlan = watch("selectedPlan");
  const terminalQuantity = watch("terminalQuantity");
  const currentPlan = filteredPlans[selectedPlan] ?? filteredPlans[0];

  const quote = useMemo(() => {
    const planPrice = currentPlan?.price || 0;
    const saasDiscountType = promoCodeData?.saasDiscountType ?? "PERCENT";
    const saasDiscountValue = promoCodeData?.discount ?? 0;
    const saasOff = promoCodeData ? saasDiscountAmount(planPrice, saasDiscountType, saasDiscountValue) : 0;
    const freeUnits = freeTerminalUnits({
      terminalQuantity,
      linkFirstTerminalFree: FIRST_TERMINAL_FREE,
      mode: promoCodeData?.freeTerminalMode,
      freeTerminalCount: promoCodeData?.freeTerminalCount,
    });
    return {
      planPrice,
      saasOff,
      saasDiscountType,
      saasDiscountValue,
      monthly: Math.max(0, planPrice - saasOff),
      freeUnits,
      terminalDue: terminalCharge(terminalQuantity, freeUnits, TERMINAL_DEVICE_PRICE),
      terminalGross: TERMINAL_DEVICE_PRICE * Math.max(0, terminalQuantity),
    };
  }, [FIRST_TERMINAL_FREE, currentPlan?.price, promoCodeData, terminalQuantity]);

  const handleQsaveToggle = (checked: boolean) => {
    setShowQsavePlans(checked);
    setValue("selectedPlan", 0);
    const firstPlan = onboardingPlans.find(
      (plan) => plan.isDiscount === showDiscountPlans && plan.type === (checked ? "QSAVE" : "STANDARD")
    );
    if (firstPlan) {
      setValue("terminalQuantity", firstPlan.minTerminalQuantity);
    }
  };

  // Validate promo code
  const { mutate: validatePromoCode } = trpc.viewer.promoCode.validate.useMutation({
    onSuccess: (data) => {
      const useDiscountPlan = data.useDiscountPlan ?? false;
      setPromoCodeData({
        code: data.code,
        description: data.description,
        discount: data.discount,
        saasDiscountType: data.saasDiscountType ?? "PERCENT",
        freeTerminalMode: data.freeTerminalMode ?? "NONE",
        freeTerminalCount: data.freeTerminalCount ?? 0,
        feeType: data.feeType ?? null,
        onlineFee: data.onlineFee ?? null,
        terminalFee: data.terminalFee ?? null,
        creditFee: data.creditFee ?? null,
        debitFee: data.debitFee ?? null,
        useDiscountPlan,
      });
      setShowDiscountPlans(useDiscountPlan);
      setPromoCodeError("");
    },
    onError: (error: { message: string }) => {
      setPromoCodeData(null);
      setPromoCodeError(error.message);
    },
  });

  const handlePromoCodeChange = (value: string) => {
    setPromoCode(value.toUpperCase());
    setPromoCodeError("");
    // Clear promo code data when user changes the input
    if (!value.trim()) {
      setPromoCodeData(null);
    }
    setShowDiscountPlans(false);
  };

  const handleApplyPromoCode = () => {
    const code = promoCode;
    if (code && code.trim().length > 0) {
      validatePromoCode({ code: code.toUpperCase() });
    } else {
      setPromoCodeError("Please enter a promo code");
    }
  };

  const { mutate: createOfficeOnboarding } = trpc.viewer.officeOnboarding.create.useMutation({
    onSuccess: () => {
      router.push("https://onboarding.quantapay.cc/signup/QuantaPay");
    },
    onError: (error) => {
      setPaymentError(error.message);
      setIsPaymentProcessing(false);
      console.error(error);
    },
  });

  const handlePayment = async (tokenId: string) => {
    const isValid = await trigger();
    if (!isValid) {
      setIsPaymentProcessing(false);
      return;
    }

    const street = getValues("address");
    const city = getValues("city");
    const state = getValues("state");
    const zip = getValues("zip");
    const fullAddress = [street, city, state, zip].filter(Boolean).join(", ");

    const requestBody = {
      firstName: getValues("firstName"),
      lastName: getValues("lastName"),
      address: fullAddress,
      email: getValues("email"),
      phone: getValues("phone"),
      contactEmail: getValues("contactEmail"),
      contactPhone: getValues("contactPhone"),
      locationName: getValues("locationName"),
      practiceManagementSoftware: getValues("practiceManagementSoftware"),
      timeZone: getValues("timeZone"),
      planId: filteredPlans[getValues("selectedPlan")].id,
      terminalQuantity: getValues("terminalQuantity"),
      tokenId: tokenId,
      isPaymentProcessing: activePaymentForm,
      isDentive: isForDentive,
      onboardingSettingId: onboardingSettings.id,
      promoCode: promoCode || undefined,
    };

    createOfficeOnboarding(requestBody);
  };
  return (
    <>
      <CustomHead title="QuantaPay | Onboarding" />
      <div className="flex justify-center bg-white">
        <div className="flex flex-col">
          <main className="mx-auto mb-10 flex w-full max-w-[1000px] flex-col items-center rounded-md border border-gray-200 ease-in-out md:mx-4">
            <div
              className="rounded-none border-gray-200 md:rounded-b-2xl"
              style={{ backgroundColor: brandColor }}>
              <div className="flex w-full items-center justify-between">
                <a
                  className="py-3 px-6 text-sm text-white md:px-8"
                  target="_blank"
                  href="https://www.quantasuite.com/get-a-demo">
                  Schedule a Demo
                </a>
                <a
                  className="py-3 px-6 text-sm text-white md:px-8"
                  target="_blank"
                  href="https://www.quantasuite.com/long-form-demo">
                  Watch a Demo Video
                </a>
              </div>
              <div className="flex flex-col items-center justify-between gap-2 rounded-t-2xl border-b border-r-gray-200 bg-[#F9FAFA] px-0 py-6 md:flex-row md:gap-0 md:px-8">
                <div className="flex w-full items-center justify-between">
                  <div className="flex w-full flex-col justify-start gap-2 md:flex-row md:items-center md:justify-between md:gap-0">
                    <div className="rounded bg-white py-1.5 px-5 md:px-0">
                      <img src={logo} alt="logo" className="h-10 w-auto p-1 md:mx-auto" />
                    </div>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 rounded-bl-2xl rounded-br-2xl md:grid-cols-7">
                <div className="col-span-4 flex flex-col gap-5 border-r-0 border-gray-200 bg-white py-8 px-3 pb-[60px] text-sm md:rounded-bl-md md:border-r md:px-8">
                  <div className="mb-4 border-b border-gray-200 pb-6">
                    <h2 className="mb-2 text-xl font-medium text-gray-800">Ready to Sign up?</h2>
                    <p className="text-sm leading-6 text-gray-600">
                      Fill out the information below to get your Quantapay account setup , you’ll be
                      processing payments in no time!
                    </p>
                    <Form
                      form={form}
                      // onBlur={onFormChange}
                      handleSubmit={() => {
                        // console.log();
                      }}>
                      {/* Contact Information Section */}
                      <div className="my-6">
                        <h3 className="text-lg font-medium text-gray-800">Contact Information</h3>
                        <p className="mb-3 text-sm leading-6 text-gray-600">
                          This person will help with implantation.
                        </p>
                        <div className="grid grid-cols-1 gap-x-3 gap-y-2 md:grid-cols-2">
                          <div>
                            <TextField label="" {...register("firstName")} placeholder="First Name" />
                          </div>
                          <div>
                            <TextField label="" {...register("lastName")} placeholder="Last Name" />
                          </div>
                          <div>
                            <TextField label="" {...register("contactEmail")} placeholder="Contact Email" />
                          </div>
                          <div>
                            <TextField
                              label=""
                              {...register("contactPhone")}
                              placeholder="Contact Phone"
                              onChange={(e) => setValue("contactPhone", formatPhoneNumber(e.target.value))}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Location Information Section */}
                      <div className="mb-6">
                        <h3 className="mb-3 text-lg font-medium text-gray-800">Location Information</h3>
                        <div className="grid grid-cols-1 gap-x-3 gap-y-2 md:grid-cols-2">
                          <div>
                            <TextField label="" {...register("locationName")} placeholder="Location Name" />
                          </div>
                          <div>
                            <Controller
                              name="practiceManagementSoftware"
                              render={({ field: { value, onChange } }) => (
                                <>
                                  <Select
                                    className="mt-2 w-full"
                                    instanceId="pmssystem"
                                    options={practiceList.filter((p) => !p.label.includes("NI"))}
                                    value={practiceList.find((p) => p.value === value)}
                                    onChange={(option) => option && onChange(option.value)}
                                    placeholder="PMS Software"
                                  />
                                </>
                              )}
                            />
                          </div>
                          <div>
                            <TextField label="" {...register("email")} placeholder="Location Email" />
                          </div>
                          <div>
                            <TextField
                              label=""
                              {...register("phone")}
                              placeholder="Location Phone"
                              onChange={(e) => setValue("phone", formatPhoneNumber(e.target.value))}
                            />
                          </div>
                          <div className="col-span-2">
                            <TextField label="" {...register("address")} placeholder="Street Address" />
                          </div>
                          <div>
                            <TextField label="" {...register("city")} placeholder="City" />
                          </div>
                          <div>
                            <TextField label="" {...register("state")} placeholder="State" />
                          </div>
                          <div>
                            <TextField label="" {...register("zip")} placeholder="ZIP" />
                          </div>
                        </div>
                      </div>
                      <div className="mt-4">
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            id="agreeToTerms"
                            {...register("agreeToTerms")}
                            className="mt-1 h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                          />
                          <label htmlFor="agreeToTerms" className="text-sm text-gray-700">
                            By checking this box, I agree to the{" "}
                            <a
                              href="https://quantasuite.com/terms"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 underline hover:text-blue-800">
                              Terms and Conditions
                            </a>
                            ,{" "}
                            <a
                              href="https://quantasuite.com/privacy-policy"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 underline hover:text-blue-800">
                              Privacy Policy
                            </a>
                            , and{" "}
                            <a
                              href="https://quantasuite.com/baa"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 underline hover:text-blue-800">
                              Business Associate Agreement (BAA)
                            </a>{" "}
                            and to receive text messages.
                          </label>
                        </div>
                        {form.formState.errors.agreeToTerms && (
                          <p className="mt-1 text-sm text-red-600">
                            {form.formState.errors.agreeToTerms.message}
                          </p>
                        )}
                      </div>
                    </Form>
                  </div>
                  {activePaymentForm && (
                    <div className="rounded-2xl border border-gray-200 p-5 md:p-5 md:py-7">
                      <h2 className="mb-3 text-xl font-medium text-gray-800">Card Input</h2>
                      <p className="text-sm leading-6 text-gray-600">
                        Input your card details to have on file for your plan
                      </p>
                      {paymentError && (
                        <p className="mt-4 rounded-md bg-red-200 py-2 px-3 text-sm text-red-700">
                          {paymentError}
                        </p>
                      )}

                      <div className="mt-6 flex flex-col items-center gap-4 border-t pt-4">
                        <PayrixForm
                          apiKey={apiKey}
                          environment={paymentEnv}
                          merchant={merchant}
                          buttonText="Process to Merchant Onboarding"
                          onSuccess={handlePayment}
                          paymentProcessing={isPaymentProcessing}
                          setPaymentError={setPaymentError}
                          setPaymentProcessing={setIsPaymentProcessing}
                        />
                      </div>
                    </div>
                  )}
                  {!activePaymentForm && (
                    <div className="mt-6 flex flex-col items-center gap-4 ">
                      <Button
                        className="flex h-12 w-full items-center justify-center gap-2 rounded-lg border-2 bg-[#1C1C1C] text-white hover:bg-[#1C1C1C]"
                        loading={isPaymentProcessing}
                        onClick={() => handlePayment("1234567890")}>
                        Process to Merchant Onboarding
                      </Button>
                    </div>
                  )}
                </div>

                <div className="md:rounded-bl-0 col-span-3 flex flex-col rounded-br-xl border-b-0 bg-white py-8 px-3 md:px-8">
                  <h2 className="mb-2 text-xl font-medium text-gray-800">Select your Plan</h2>
                  <p className="mb-2 text-sm text-gray-600">Select your Quantapay Plan below</p>
                  {!onboardingSettings.hideToggleSwitch && (
                    <div className="mb-5 flex items-center gap-2">
                      <Switch
                        checked={showQsavePlans}
                        onCheckedChange={handleQsaveToggle}
                        label="QSave Surcharge Plan"
                      />
                    </div>
                  )}
                  <RadioGroup.Root
                    key={showDiscountPlans ? "qsave-discount" : showQsavePlans ? "qsave" : "standard"}
                    defaultValue="0"
                    onValueChange={(value) => setValue("selectedPlan", +value)}>
                    {filteredPlans.map((plan, index) => (
                      <div
                        key={plan.id}
                        className="my-2 flex items-center justify-between rounded-md border border-gray-200 px-3 py-4">
                        <div>
                          <div className="mb-1 flex flex-row  gap-4">
                            <img src={plan.logo} alt="logo" className="h-[30px] w-[30px]" />
                            <div className="flex flex-col gap-2">
                              <div className="">
                                <h2 className="mb-1 text-lg font-medium">
                                  {plan.name} - {plan.price ? `$${plan.price}/month` : "Free"}
                                </h2>
                                <p className="mb-1 text-xs text-gray-600">{plan.description}</p>
                                {(plan.detailContent as Array<string>).map((item: string, index: number) => (
                                  <p key={index} className="text-[10px]  text-gray-600">
                                    • {item}
                                  </p>
                                ))}
                                {promoCodeData &&
                                  (showQsavePlans
                                    ? hasPromoFeePair(promoCodeData.creditFee, promoCodeData.debitFee)
                                    : hasPromoFeePair(
                                        promoCodeData.onlineFee,
                                        promoCodeData.terminalFee
                                      )) && (
                                    <>
                                      <span className="text-[10px] text-gray-600">Promo code fees:</span>
                                      <p className="text-[10px] text-gray-600">
                                        {showQsavePlans
                                          ? `• Credit ${formatFeePct(
                                              promoCodeData.creditFee
                                            )} · Debit ${formatFeePct(promoCodeData.debitFee)}`
                                          : `• Online ${formatFeePct(
                                              promoCodeData.onlineFee
                                            )} · Terminal ${formatFeePct(promoCodeData.terminalFee)}`}
                                      </p>
                                    </>
                                  )}
                              </div>
                            </div>
                          </div>
                          <div className="text-[10px] leading-6 text-gray-600">
                            <p className="">
                              *Must add a credit card terminal (minimum {plan.minTerminalQuantity} device)
                            </p>
                          </div>
                        </div>
                        <RadioGroup.Item
                          className="min-w-4 flex h-5 w-5 cursor-pointer items-center rounded-full border border-blue-500 bg-white focus:border-2 focus:outline-none ltr:mr-2 rtl:ml-2"
                          value={index.toString()}
                          id="r1">
                          <RadioGroup.Indicator className="relative flex h-5 w-5 items-center justify-center after:block after:h-3 after:w-3 after:rounded-full after:bg-blue-500" />
                        </RadioGroup.Item>
                      </div>
                    ))}
                  </RadioGroup.Root>
                  <div className="my-4 border-t border-gray-200" />
                  <h2 className="mb-2 text-xl font-medium text-gray-800">Promo Code</h2>
                  <div className="mb-4">
                    <div className="flex gap-2">
                      <TextField
                        label=""
                        value={promoCode}
                        placeholder="Enter promo code"
                        onChange={(e) => handlePromoCodeChange(e.target.value)}
                        className="flex-1"
                      />
                      <Button
                        className="h-9 rounded-lg border bg-[#1C1C1C] text-white hover:bg-[#1C1C1C]"
                        onClick={handleApplyPromoCode}
                        disabled={promoCode.trim().length === 0}>
                        Apply
                      </Button>
                    </div>
                    {promoCodeError && <p className="mt-1 text-sm text-red-600">{promoCodeError}</p>}
                    {promoCodeData && (
                      <div className="mt-1 space-y-2">
                        <p className="text-sm text-green-600">
                          {promoCodeData.description ? promoCodeData.description : "Promo code applied!"}
                        </p>
                        {(promoCodeData.discount > 0 ||
                          promoCodeData.freeTerminalMode !== "NONE" ||
                          (showQsavePlans
                            ? hasPromoFeePair(promoCodeData.creditFee, promoCodeData.debitFee)
                            : hasPromoFeePair(promoCodeData.onlineFee, promoCodeData.terminalFee))) && (
                          <div className="rounded-md border border-green-200 bg-green-50/80 p-3 text-sm text-gray-800">
                            <p className="mb-2 font-medium text-gray-900">Promo details</p>
                            <ul className="space-y-1.5 text-gray-700">
                              {promoCodeData.discount > 0 && (
                                <li className="flex justify-between gap-4">
                                  <span className="text-gray-600">SaaS discount</span>
                                  <span className="font-medium">
                                    {formatSaasDiscountValue(
                                      promoCodeData.saasDiscountType,
                                      promoCodeData.discount
                                    )}
                                  </span>
                                </li>
                              )}
                              {promoCodeData.freeTerminalMode !== "NONE" && (
                                <li className="flex justify-between gap-4">
                                  <span className="text-gray-600">Free terminals</span>
                                  <span className="font-medium">
                                    {formatFreeTerminalSummary(
                                      promoCodeData.freeTerminalMode,
                                      promoCodeData.freeTerminalCount
                                    )}
                                  </span>
                                </li>
                              )}
                              {showQsavePlans &&
                                hasPromoFeePair(promoCodeData.creditFee, promoCodeData.debitFee) && (
                                  <>
                                    <li className="flex justify-between gap-4">
                                      <span className="text-gray-600">Credit fee</span>
                                      <span className="font-medium">
                                        {formatFeePct(promoCodeData.creditFee)}
                                      </span>
                                    </li>
                                    <li className="flex justify-between gap-4">
                                      <span className="text-gray-600">Debit fee</span>
                                      <span className="font-medium">
                                        {formatFeePct(promoCodeData.debitFee)}
                                      </span>
                                    </li>
                                  </>
                                )}
                              {!showQsavePlans &&
                                hasPromoFeePair(promoCodeData.onlineFee, promoCodeData.terminalFee) && (
                                  <>
                                    <li className="flex justify-between gap-4">
                                      <span className="text-gray-600">Online fee</span>
                                      <span className="font-medium">
                                        {formatFeePct(promoCodeData.onlineFee)}
                                      </span>
                                    </li>
                                    <li className="flex justify-between gap-4">
                                      <span className="text-gray-600">Terminal fee</span>
                                      <span className="font-medium">
                                        {formatFeePct(promoCodeData.terminalFee)}
                                      </span>
                                    </li>
                                  </>
                                )}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="my-4 border-t border-gray-200" />
                  <h2 className="mb-2 text-xl font-medium text-gray-800">Select your Device and Quantity</h2>
                  <p className="mb-5 text-sm text-gray-600">Select the device and quantity you need</p>
                  <div className="flex gap-4 rounded-md border border-gray-200 p-4">
                    <img src="/onboarding/m5000.svg" alt="credit card" className="h-[113px] w-[55px]" />
                    <div className="flex-1">
                      <h2 className="mb-1 text-lg font-medium">Move/5000</h2>
                      <p className="text-xs text-gray-600">Everything you need to process payments</p>
                      {freeTerminalMessage(quote.freeUnits, terminalQuantity) && (
                        <p className="text-xs font-medium text-green-600">
                          {freeTerminalMessage(quote.freeUnits, terminalQuantity)}
                        </p>
                      )}
                      <div className="mt-4 flex items-center">
                        <QuantitySelector
                          key={currentPlan?.id}
                          initialValue={currentPlan?.minTerminalQuantity ?? 1}
                          minValue={currentPlan?.minTerminalQuantity ?? 1}
                          maxValue={10}
                          onQuantityChange={(quantity) => setValue("terminalQuantity", quantity)}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Total Price Section */}
                  <div className="mt-4 rounded-md border border-gray-200 bg-gray-50 p-4">
                    <h3 className="mb-3 text-lg font-medium text-gray-800">Total Price</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Plan Price:</span>
                        <span className="font-medium">${quote.planPrice}/month</span>
                      </div>
                      {quote.saasOff > 0 && (
                        <div className="flex justify-between text-green-600">
                          <span>
                            SaaS discount (
                            {formatSaasDiscountValue(quote.saasDiscountType, quote.saasDiscountValue)}):
                          </span>
                          <span className="font-medium">-${quote.saasOff.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-gray-600">
                          Terminal Devices ({terminalQuantity} × ${TERMINAL_DEVICE_PRICE}):
                        </span>
                        <span className="font-medium">${quote.terminalGross.toFixed(2)}</span>
                      </div>
                      {quote.freeUnits > 0 && (
                        <div className="flex justify-between text-green-600">
                          <span>Free terminals ({quote.freeUnits}):</span>
                          <span className="font-medium">
                            -${(quote.terminalGross - quote.terminalDue).toFixed(2)}
                          </span>
                        </div>
                      )}
                      <div className="border-t border-gray-300 pt-2">
                        <div className="flex justify-between text-base font-semibold">
                          <span>Total:</span>
                          <span>
                            ${quote.monthly.toFixed(2)}/month + ${quote.terminalDue.toFixed(2)} one-time
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </main>
          <div className="mx-auto my-5 flex w-full items-center justify-center gap-3 md:w-3/4">
            <GreenShieldCheckIcon />

            <p className="text-sm leading-6 text-gray-600">
              Payments are secured using TLS & SSL encryption.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

export const getServerSideProps = async (_context: GetServerSidePropsContext) => {
  const { tag } = _context.query;
  let subLink = undefined;

  if (tag && Array.isArray(tag) && tag.length === 1) {
    subLink = tag[0];
  } else if (tag === undefined) {
    subLink = "default";
  } else {
    return {
      notFound: true,
    };
  }
  const onboardingSettings = await prisma.onboardingSettings.findFirst({
    where: {
      tag: subLink,
    },
    select: {
      id: true,
      logo: true,
      brandColor: true,
      isFirstTerminalFree: true,
      activePaymentForm: true,
      isForDentive: true,
      hideToggleSwitch: true,
      onboardingPlans: {
        select: {
          id: true,
          name: true,
          description: true,
          detailContent: true,
          price: true,
          minTerminalQuantity: true,
          logo: true,
          type: true,
          isDiscount: true,
        },
        orderBy: {
          id: "asc",
        },
      },
    },
  });

  if (
    !onboardingSettings ||
    process.env.ACTIVE_SIGNUP_LINK !== "true" ||
    !process.env.PAYRIX_PUBLIC_API_KEY ||
    !process.env.PAYRIX_ENVIRONMENT ||
    !process.env.ADMIN_MERCHANT_ID ||
    !onboardingSettings.onboardingPlans.length
  ) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      apiKey: process.env.PAYRIX_PUBLIC_API_KEY || "",
      paymentEnv: process.env.PAYRIX_ENVIRONMENT || "sandbox",
      merchant: process.env.ADMIN_MERCHANT_ID || "",
      onboardingSettings,
    },
  };
};
